import { useEffect, useRef, useState } from "react";
import { ChevronDown } from "lucide-react";
import { WEEKDAYS } from "./schedulerTypes";

interface Props {
  /** Comma-separated selected days, e.g. "Monday,Friday". */
  value: string;
  onChange: (value: string) => void;
}

/**
 * Multi-select weekday picker for the "Import On" field when the frequency is
 * Weekly — a button that opens a checkbox panel. Mirrors g360's radix Popover
 * version without pulling in a new dependency.
 */
export function WeekdayMultiSelect({ value, onChange }: Readonly<Props>) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  const selectedDays = value
    .split(",")
    .map((d) => d.trim())
    .filter(Boolean);

  useEffect(() => {
    if (!open) return;
    const onDocClick = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [open]);

  const toggleDay = (day: string) => {
    const next = selectedDays.includes(day)
      ? selectedDays.filter((d) => d !== day)
      : [...selectedDays, day];
    onChange(next.join(","));
  };

  const summary =
    selectedDays.length === 0
      ? "Select days"
      : selectedDays.length <= 2
        ? selectedDays.join(", ")
        : `${selectedDays.length} days selected`;

  return (
    <div ref={rootRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        className="flex w-full items-center justify-between gap-2 text-sm bg-[#f5f7fb] dark:bg-white/[0.04] rounded-lg px-3 py-2 outline-none border border-[#e2e8f0] dark:border-white/[0.07] focus:border-indigo-500 dark:focus:border-indigo-500 transition-all text-[#0f172a] dark:text-white"
      >
        <span className={selectedDays.length ? "" : "text-[#64748b] dark:text-white/25"}>
          {summary}
        </span>
        <ChevronDown className="size-4 flex-shrink-0 text-muted-foreground" />
      </button>
      {open && (
        <div className="absolute z-50 mt-1 w-full rounded-lg border border-border bg-card p-1 shadow-xl">
          {WEEKDAYS.map((day) => (
            <label
              key={day}
              className="flex cursor-pointer items-center gap-2 rounded-md px-2.5 py-1.5 text-sm text-foreground hover:bg-accent"
            >
              <input
                type="checkbox"
                checked={selectedDays.includes(day)}
                onChange={() => toggleDay(day)}
                className="size-3.5 accent-indigo-600"
              />
              <span>{day}</span>
            </label>
          ))}
        </div>
      )}
    </div>
  );
}
